// Layer 4 (UI) — the Asset register tab (a20-maintenance S6a; polish.P21 comp
// A with variant C's BM01 row treatment). The tab is the register card, then
// the shutdown windows beside the FMEA task types.
//
// Everything on this tab is derived: every cell of the items table is a field
// of `assetRegisterRows()`, the highlighted row is the item carrying the route's
// leading monitoring point (`leadingItemId`), and its Condition cell selects
// that item's worst point on Condition monitoring (`worstPointForItem`). The
// tab runs no projection of its own — it passes the sources in and renders
// what comes back. The KPI strip it carried moved into the route's one status
// band (adjudication 4).
//
// Two things moved rather than disappeared. The per-item condition signal is a
// long descriptive string; it is the item cell's `title` now, which is the
// depth rung, and the `Condition` column carries the chip instead. The
// criticality cell keeps its pill and gains the register's RPN beside it.
//
// The ten columns are the height-and-width budget. Three of them wrap —
// the item cell (clamped to two lines), `FMEA task type` and `Frequency`;
// the other six are `whitespace-nowrap`. The item cell carries a floor so
// its two-line label keeps its width. `Open work orders` holds its own floor
// (6.5rem): narrower, its three words break onto three lines.

import {
  Pill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { useDrawer } from '@/app/providers';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { DowntimeCard, RankBar } from '../downtime/DowntimeCard';
import { EDGE_TABLE, LEAD_ROW } from './MaintenanceTable';
import {
  assetRegisterRows,
  conditionTone,
  criticalityPillTone,
  itemsByCriticality,
  leadingItemId,
  shutdownWindows,
  taskTypeDistribution,
  worstPointForItem,
  type AssetRegisterRow,
} from './Strategy.transform';
import { pmTasks } from './PmSchedule.transform';
import { conditionMonitoringPoints } from './Scene.transform';

/** The criticality cell: the tier pill, the register id and its RPN, or the
 *  dash when the item has no register row. */
function criticalityCell(row: AssetRegisterRow) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-[1400px]:flex-nowrap">
      <Pill tone={criticalityPillTone(row.criticality)}>{row.criticality}</Pill>
      {row.riskLabel ? (
        <span className="whitespace-nowrap font-mono text-xs text-muted">{row.riskLabel}</span>
      ) : (
        <span className="text-xs text-muted" title="No a20-risk register row for this item">
          —
        </span>
      )}
    </span>
  );
}

/** The measured dash a cell shows when the item has nothing of that kind on the
 *  schedule — the same idiom the criticality cell uses for a missing register
 *  row, and the same wording in every PM cell. */
function noPmDash() {
  return (
    <span className="text-muted" title="No PM instance on the schedule">
      —
    </span>
  );
}

export function StrategyTab({ onSelectPoint }: { onSelectPoint: (pointId: string) => void }) {
  const { open: openDrawer } = useDrawer();
  const { state } = useScenario();
  const cursor = state.cursor;
  const tasks = pmTasks();
  const points = conditionMonitoringPoints(state);
  const rows = assetRegisterRows(itemsByCriticality(), tasks, points, cursor);
  const leadId = leadingItemId(points);
  const windows = shutdownWindows();
  const distribution = taskTypeDistribution();

  return (
    <Commentable name="Strategy">
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <DowntimeCard
          title="Maintainable items"
          meta="criticality sorted"
          actions={
            <ProvenanceChip
              text="[syn]"
              title="Criticality reads the risk register's RK id and RPN band; RK-02, RK-01, RK-04, RK-05, RK-08 and RK-09 are the cross-stream register rows, the remaining four items have no register row and carry this stream's own [syn] read. Area decodes the flowsheet subprocess the risk register's own circuit column names."
              compact
            />
          }
          className="px-0 pb-1 xl:col-span-2 [&>header]:px-4"
        >
          <div className={EDGE_TABLE} data-asset-register>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH>Item</TH>
                  <TH className="w-[6.5rem] !whitespace-normal" data-register-column="open-work-orders">Open work orders</TH>
                  <TH>Area</TH>
                  <TH>Criticality</TH>
                  <TH>FMEA task type</TH>
                  <TH>Frequency</TH>
                  <TH>Last PM</TH>
                  <TH>Next PM</TH>
                  <TH>Condition</TH>
                  <TH className="max-[1399px]:w-[5.5rem] max-[1399px]:!whitespace-normal">Next shutdown</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => {
                  const lead = row.id === leadId;
                  const worstPoint = lead ? worstPointForItem(row.id, points) : null;
                  return (
                    <TR key={row.id} className={lead ? LEAD_ROW : undefined} data-register-row={row.id} data-register-lead={lead || undefined}>
                      <TD
                        className="min-w-[8.5rem] font-semibold !text-ink-primary min-[1400px]:line-clamp-2 min-[1400px]:min-w-[10rem]"
                        title={row.conditionSignal}
                      >
                        {row.label}
                      </TD>
                      <TD className="tabular-nums whitespace-nowrap">
                        {row.openWorkOrderCount > 0 ? (
                          <button
                            type="button"
                            className="font-semibold text-accent-700 hover:underline dark:text-accent-400"
                            onClick={() =>
                              openDrawer('work-order-list', {
                                kicker: 'Asset',
                                title: row.label,
                                wide: true,
                                data: { assetId: row.id },
                              })
                            }
                          >
                            {row.openWorkOrderCount}
                          </button>
                        ) : (
                          row.openWorkOrderCount
                        )}
                      </TD>
                      <TD className="min-[1400px]:whitespace-nowrap">{row.areaLabel}</TD>
                      <TD>{criticalityCell(row)}</TD>
                      <TD className="tabular">{row.taskType}</TD>
                      <TD className="tabular">{row.frequency}</TD>
                      <TD className="tabular whitespace-nowrap">
                        {row.lastPmLabel === '—' ? noPmDash() : row.lastPmLabel}
                      </TD>
                      <TD className="min-[1400px]:whitespace-nowrap">
                        <span className="whitespace-nowrap">{row.nextPmLabel === '—' ? noPmDash() : row.nextPmLabel}</span>
                        {row.overdue ? (
                          <span className="block min-[1400px]:ml-1.5 min-[1400px]:inline">
                            <Pill tone="danger">Overdue</Pill>
                          </span>
                        ) : null}
                      </TD>
                      <TD className="whitespace-nowrap">
                        {row.condition === null ? (
                          <span className="text-muted" title="No condition-monitoring point">
                            —
                          </span>
                        ) : worstPoint ? (
                          <button
                            type="button"
                            onClick={() => onSelectPoint(worstPoint)}
                            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
                            title="Condition monitoring"
                            data-register-condition-link={worstPoint}
                          >
                            <Pill tone={conditionTone(row.condition)}>{row.conditionLabel}</Pill>
                          </button>
                        ) : (
                          <Pill tone={conditionTone(row.condition)}>{row.conditionLabel}</Pill>
                        )}
                      </TD>
                      <TD className="tabular whitespace-nowrap">
                        {row.nextShutdownLabel === '—' ? (
                          <span className="text-muted" title="No shutdown window on the schedule">
                            —
                          </span>
                        ) : (
                          row.nextShutdownLabel
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>

        <DowntimeCard
          title="Shutdown windows"
          actions={
            <ProvenanceChip
              text="[syn]"
              title="No shutdown calendar exists in the document set (v19 M9 §6) — reformatted from the PM Schedule tab's real rows"
              compact
            />
          }
          className="px-0 pb-1 [&>header]:px-4"
        >
          <div className={EDGE_TABLE}>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH>Area</TH>
                  <TH>Driver</TH>
                  <TH>Start</TH>
                  <TH className="text-right">Duration</TH>
                </TR>
              </THead>
              <TBody>
                {windows.map((window) => (
                  <TR key={window.id}>
                    <TD className="whitespace-nowrap font-semibold !text-ink-primary">{window.area}</TD>
                    <TD>{window.driver}</TD>
                    <TD className="tabular whitespace-nowrap">{window.plannedStart}</TD>
                    <TD className="tabular whitespace-nowrap text-right">{window.durationHours}h</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>

        <DowntimeCard title="FMEA task types" meta="occurrences in the FMEA">
          <div className="flex flex-col gap-2" data-task-type-bars>
            {distribution.map((slice) => (
              <div key={slice.taskType} className="grid grid-cols-[13.5rem_minmax(0,1fr)_2rem] items-center gap-3 text-[12.5px]">
                <span className="truncate font-semibold text-ink-primary">{slice.taskType}</span>
                <RankBar ratio={slice.pct / 100} tone="ink" />
                <span className="text-right font-semibold tabular-nums text-ink-primary">{slice.occurrences}</span>
              </div>
            ))}
          </div>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}
